// TODO - Type 'any' needs to be fixed.
import {CircularProgress} from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { GridRowId } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro-6";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import SkeletonLoadingOverlay from "../../../../utils/use-skeleton-loading-overlay";
import useViewport from "../../../../utils/use-viewport";
import useSearch from "../../controllers/use-search";

interface ShiftDetailsTableProps {
  parentHeight?: number;
  rows: any;
  columns: any;
  isShiftPast: boolean;
  setWorkersSelected: React.Dispatch<React.SetStateAction<GridRowId[]>>;
  readOnly?: boolean;
  searchValue: string;
  hasOpenSpots: boolean;
  statusOptions: any;
  loadingStatus: string;
  activeTab?: number;
}

const ShiftDetailsTable: React.FC<ShiftDetailsTableProps> = ({
  parentHeight,
  rows,
  columns,
  isShiftPast,
  setWorkersSelected,
  readOnly = false,
  searchValue = "",
  hasOpenSpots,
  statusOptions,
  loadingStatus,
  activeTab = 1,
}) => {
  const { t } = useTranslation();
  const [selectionModel, setSelectionModel] = React.useState<any>([]);
  const filteredRowsBySearch = useSearch({
    data: rows,
    searchValue,
    searchField: "workerName",
  });
  const filteredRowsByStatus = useMemo(() => {
    return filteredRowsBySearch.filter((row: any) => {
      if (statusOptions.every((status: any) => !status.checked)) {
        return true; // If no filters are checked, show all rows
      }
      return statusOptions.some((status: any) => {
        return status.checked && status.label === row.status;
      });
    });
  }, [filteredRowsBySearch, statusOptions]);

  const canSelectRows = !isShiftPast && !readOnly && hasOpenSpots;

  // Hiding the checkboxes alone isn't enough: a row click still selects any
  // row this allows, which would let a full shift be renotified.
  const isRowSelectable = useCallback(
    (params: { row: { status: string } }) =>
      canSelectRows
      && ["Notified", "Viewed", "Not Notified"].includes(params.row.status),
    [canSelectRows]
  );

  // Refreshed rows (e.g. after a renotify) clear the grid's selection, so
  // clear the parent's copy too or its actions keep acting on stale workers.
  // Keep the same array when already empty: the Confirmed tab passes freshly
  // filtered rows on every render, and a new [] would re-render forever.
  // Also clear it when the shift fills up while workers are selected.
  useEffect(() => {
    setSelectionModel([]);
    setWorkersSelected((previous) => (previous.length === 0 ? previous : []));
  }, [rows, canSelectRows, setWorkersSelected]);
  const { height } = useViewport();

  if (loadingStatus === "pending" && activeTab === 0) {
    return <Box width={1150} mr="auto" ml="auto" height={parentHeight ? parentHeight - 127 : height - 127} position="relative">
      <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        marginTop: "32px",
      }}>
        <CircularProgress />
      </Box>
    </Box>
  } else {
    return (
      <Box width={1150} mr="auto" ml="auto" height={parentHeight ? parentHeight - 127 : height - 127}>
      <DataGridPro
        rows={filteredRowsByStatus}
        columns={columns}
        isRowSelectable={isRowSelectable}
        checkboxSelection={canSelectRows}
        hideFooter={true}
        loading={loadingStatus === "pending"}
        slots={{
          loadingOverlay: () => (
            <SkeletonLoadingOverlay
              columnData={{
                columnName: "workerName",
                columnStyle: { marginLeft: "12px" },
              }}
            />
          ),
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              {t("scheduler_no-workers-found")}
            </Stack>
          ),
        }}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection);
          setWorkersSelected(newSelection);
        }}
        sx={{
          border: 0,
          marginBottom: "-20px",
          overflowX: "hidden",
                height: parentHeight ? parentHeight - 127 : height - 127,
          minHeight: 200,
          // "& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator": {
          //     display: "none"
          // },
          "& .MuiDataGrid-checkboxInput": {
            margin: "auto", // Center the checkbox vertically
          },
          "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput": {
            margin: "auto", // Center the header checkbox vertically
          },
          "& .MuiDataGrid-columnHeaderTitleContainerContent": {
            width: "100%",
          },
          "& .MuiDataGrid-columnSeparator": {
            opacity: "0 !important",
          },
          "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
            {
              outline: "none !important",
            },
            "&.hover-column-header": {
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "& .MuiDataGrid-sortIcon": {
                display: "none",
              },
            },
            ".MuiDataGrid-cell": {
              "&:focus": {
                outline: "none",
              },
              "&.hover-column-cell": {
                position: "sticky",
                right: 0,
                zIndex: 1,
              },
              "& .hover-content": {
                transition: "opacity 0.3s ease-in-out",
                opacity: 0,
              },
            },
            ".MuiDataGrid-row:hover .hover-column-cell .hover-content": {
              opacity: 1,
              backgroundColor: "#f5f5f5",
            },
            "& .MuiDataGrid-row.Mui-selected .hover-column-cell .hover-content": {
              backgroundColor: "#e7e9f0",
            },
        }}
      />
      </Box>
    );
}

};

export default ShiftDetailsTable;
